import React from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '../components/PageSEO';
import ContentLayout from '../components/ContentLayout';

const ReduceLlmTokenCosts: React.FC = () => (
  <>
    <PageSEO
      path="/reduce-llm-token-costs"
      title="How to Reduce LLM Token Costs: 7 Practical Techniques (2026)"
      description="Cut your OpenAI, Anthropic, and Gemini bills with seven proven techniques to reduce LLM token usage — including converting structured data to TOON for 30-60% savings."
      keywords="reduce LLM token costs, lower OpenAI bill, cut GPT-4 cost, reduce token usage, LLM cost optimization"
    />
    <ContentLayout
      related={[
        { to: '/what-is-toon', label: 'What is TOON? A plain-English guide' },
        { to: '/json-vs-toon', label: 'JSON vs TOON: a side-by-side comparison' },
        { to: '/toon-format-spec', label: 'TOON format spec and syntax rules' },
      ]}
    >
      <h1>How to reduce LLM token costs</h1>
      <p className="content-lead">
        Token bills add up fastest where you least expect them — in the structured data you paste
        into prompts. Here are seven techniques that actually move the number, ordered from highest
        to lowest leverage for most applications.
      </p>

      <h2>1. Compress structured data with TOON</h2>
      <p>
        If your prompts include JSON — API responses, database rows, tool outputs, RAG context —
        this is usually the biggest single win. Converting that JSON to{' '}
        <Link to="/what-is-toon">TOON</Link> removes the braces, brackets, commas, and repeated
        field names that JSON spends tokens on, typically cutting 30-60% off the structured portion
        of the prompt with no loss of information.
      </p>

      <h2>2. Trim the context you actually send</h2>
      <p>
        Most prompts carry data the model never needs. Drop unused fields before sending, summarize
        long documents instead of pasting them whole, and select only the top-k retrieved chunks in
        a RAG pipeline rather than everything that matched.
      </p>

      <h2>3. Use prompt caching</h2>
      <p>
        OpenAI, Anthropic, and Google all support caching a stable prompt prefix. Put your fixed
        system instructions and schema at the top so repeated calls read them from cache at a
        fraction of the price, and vary only the part that changes per request.
      </p>

      <h2>4. Right-size the model</h2>
      <p>
        Route easy, high-volume calls to a smaller, cheaper model and reserve the frontier model for
        the requests that genuinely need it. A tiered setup often cuts cost more than any prompt
        tweak, because the bulk of traffic moves to a cheaper tier.
      </p>

      <h2>5. Batch where latency allows</h2>
      <p>
        Most providers offer a batch API at roughly half price for work that does not need an
        instant response — overnight enrichment, evals, bulk classification. If it is not
        user-facing, batch it.
      </p>

      <h2>6. Cap output length</h2>
      <p>
        Output tokens cost more than input tokens. Set a sensible <code>max_tokens</code>, ask for
        concise answers, and request structured output so the model does not pad the response with
        prose you will throw away.
      </p>

      <h2>7. Measure before and after</h2>
      <p>
        You cannot optimize what you do not count. Log token usage per request type, find the few
        endpoints that dominate the bill, and apply the techniques above there first.
      </p>

      <div className="content-callout">
        <p>
          <strong>Start with the easy win:</strong> paste a representative payload into the{' '}
          <Link to="/">converter</Link> to see your exact TOON savings on your real data before you
          change anything in your prompts.
        </p>
      </div>
    </ContentLayout>
  </>
);

export default ReduceLlmTokenCosts;
